import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HighlightOff from '@mui/icons-material/HighlightOff';

type ViewMode = 'compact' | 'extended';

type AnalyticsDialogProps = {
  open: boolean;
  onClose: () => void;
  initialViewMode?: ViewMode;
  // Accepted for backwards compatibility with callers (e.g.
  // InventoryHistoryAnalyticsDialog) that pass a controlled `viewMode`, but
  // this component only seeds its internal state from `initialViewMode` and
  // manages `viewMode` itself thereafter -- this prop is otherwise unused.
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  title?: string;
  children?: React.ReactNode;
  compactWidth?: number;
  compactHeight?: number;
  extendedWidth?: number;
  extendedHeight?: number;
  enableShowMore?: boolean;
};

export default function AnalyticsDialog({
  open,
  onClose,
  initialViewMode = 'compact',
  onViewModeChange,
  title = 'Analytics',
  children,
  compactWidth,
  compactHeight,
  extendedWidth,
  extendedHeight,
  enableShowMore
}: AnalyticsDialogProps) {
  const theme = useTheme();

  const prefersXsFullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const toggleViewMode = useCallback(() => {
    const nextMode: ViewMode = viewMode === 'compact' ? 'extended' : 'compact';
    setViewMode(nextMode);
    if (onViewModeChange) onViewModeChange(nextMode);
  }, [viewMode, onViewModeChange]);

  const desiredWidth =
    viewMode === 'compact' ? compactWidth : extendedWidth;
  const desiredHeight =
    viewMode === 'compact' ? compactHeight : extendedHeight;

  const updateFullScreen = useCallback(() => {
    if (desiredWidth == null && desiredHeight == null) {
      setIsFullScreen(prefersXsFullScreen);
      return;
    }

    const exceedsWidth =
      desiredWidth != null && desiredWidth > window.innerWidth;
    const exceedsHeight =
      desiredHeight != null && desiredHeight > window.innerHeight;

    setIsFullScreen(exceedsWidth || exceedsHeight);
  }, [desiredWidth, desiredHeight, prefersXsFullScreen]);

  useEffect(() => {
    updateFullScreen();
    window.addEventListener('resize', updateFullScreen);

    return () => window.removeEventListener('resize', updateFullScreen);
  }, [updateFullScreen]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isFullScreen}
      className='analytics-dialog'
      slotProps={{
        paper: {
          style: !isFullScreen
            ? {
              width: desiredWidth,
              height: desiredHeight,
              maxWidth: 'none',
            }
            : {},
        }
      }}
    >
      <div>
        <Typography variant="h6">
          {title}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="large">
          <HighlightOff style={{ color: 'red' }} />
        </IconButton>
      </div>
      <DialogContent>{children}</DialogContent>
      {enableShowMore && (
        <>
          <DialogActions>
            <Button variant="text" onClick={toggleViewMode}>
              {viewMode === 'compact' ? 'Show More' : 'Show Less'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog >
  );
}
